import React, { useState } from 'react';
import { BadgeCheckIcon, ClockIcon, StarIcon, TriangleAlertIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { ReportControls } from '../trust/ReportControls';
import { FeatureGate } from '../platform/FeatureStatusBadge';
import { StandingChip } from '../verification/StandingChip';
import { standingFor } from '../../data/standings';
import { naira } from '../../utils/format';
import type { ServiceProvider } from '../../types/marketplace';
export function ProviderCard({
  provider
}: {
  provider: ServiceProvider;
}) {
  const [hireOpen, setHireOpen] = useState(false);
  const [packageId, setPackageId] = useState(provider.packages[0]?.id ?? '');
  const selected = provider.packages.find(item => item.id === packageId);
  const fee = selected ? Math.round(selected.priceNaira * 0.05) : 0;
  return <article className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-3104" data-mp="15kmliyb07dw6">
      <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-3105" data-mp="2yoibbs7q25up">
        <div className="flex min-w-0 gap-3" data-id="element-3106" data-mp="2ymfcciold2du">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[13px] font-semibold text-brand-800" data-id="element-3107" data-mp="2tqgjoiw8cy7m">
            {provider.initials}
          </span>
          <div className="min-w-0" data-id="element-3108" data-mp="33nl52gi94pnd">
            <div className="flex flex-wrap items-center gap-2" data-id="element-3109" data-mp="33mju0c18k35n">
              <h3 className="text-[15px] font-semibold text-ink" data-id="element-3110" data-mp="3hehu5xcyyopo">{provider.displayName}</h3>
              <span className="cz-num text-[13px] text-ink-subtle" data-id="element-3111" data-mp="3deft9qf25zbg">@{provider.handle}</span>
              {provider.identityVerified ? <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-3113" data-mp="3rb31ipcgobwo" />} data-id="element-3112" data-mp="3rcnyelm906xp">
                  Identity verified
                </Badge> : <Badge tone="warn" icon={<TriangleAlertIcon className="h-3.5 w-3.5" data-id="element-3115" data-mp="1wzy3c0k58roq" />} data-id="element-3114" data-mp="1wymq7a4t67ky">
                  Unverified provider
                </Badge>}
              {provider.mentorship && <Badge tone="brand" data-id="element-3116" data-mp="3s345xx3mmbwj">Mentorship</Badge>}
            </div>
            {/* Academic standing is a separate claim from identity, and from
                being a competent seller. Three signals, three statements. */}
            <div className="mt-1.5" data-id="element-3117" data-mp="4x3jg20h59vo">
              <StandingChip standing={standingFor(provider.handle)} data-id="element-3118" data-mp="507spdssqvt7" />
            </div>
            <p className="mt-1.5 text-[13px] text-ink-muted" data-id="element-3119" data-mp="9uv88n5tofvn">{provider.headline}</p>
            <p className="mt-1 text-[12px] text-ink-subtle" data-id="element-3120" data-mp="evj8lzfxy92j">
              {provider.category} · {provider.subcategory}
            </p>
          </div>
        </div>
        <div className="shrink-0 text-right" data-id="element-3121" data-mp="olm01v7ezeyr">
          <p className="cz-num inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink" data-id="element-3122" data-mp="onysj2ast7q2">
            <StarIcon className="h-3.5 w-3.5" data-id="element-3123" data-mp="spjzk70nirsc" />
            {provider.rating.toFixed(1)}
          </p>
          <p className="cz-num text-[12px] text-ink-subtle" data-id="element-3124" data-mp="xkfvhi90dzaq">
            {provider.reviewCount} reviews · {provider.completedOrders} orders
          </p>
        </div>
      </div>

      <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-3125" data-mp="1cr53j5rjeefl">
        {provider.skills.map(skill => <li key={skill} data-id="element-3126" data-mp="17ow904z0n4bd">
            <Badge data-id="element-3127" data-mp="17stv8zgw4bzh">{skill}</Badge>
          </li>)}
      </ul>

      <ul className="mt-4 space-y-2 border-t border-line pt-3" data-id="element-3128" data-mp="12rwd2mvvrhxz">
        {provider.packages.map(servicePackage => <li key={servicePackage.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line px-3 py-2.5" data-id="element-3129" data-mp="yrvpabmw5ue9">
            <div className="min-w-0" data-id="element-3130" data-mp="1qhid1ck2u171">
              <p className="text-[13px] font-medium text-ink" data-id="element-3131" data-mp="1lq8okfa9l3wq">{servicePackage.name}</p>
              <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-3132" data-mp="1lnvwvaepff6h">
                {servicePackage.sessionMinutes ? `${servicePackage.sessionMinutes} minute session` : `Delivery in ${servicePackage.deliveryDays} days`}{' '}
                · {servicePackage.includes.join(', ')}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3" data-id="element-3133" data-mp="1twrvumgvk4io">
              <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-3134" data-mp="1ywmpgxs1b87f">
                {naira(servicePackage.priceNaira)}
              </span>
              <Button size="sm" variant="secondary" onClick={() => {
            setPackageId(servicePackage.id);
            setHireOpen(true);
          }} data-id="element-3135" data-mp="1kyg7kr3hdite">
                {servicePackage.sessionMinutes ? 'Book' : 'Order'}
              </Button>
            </div>
          </li>)}
      </ul>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3" data-id="element-3136" data-mp="23uxyx7ef3gp4">
        <p className="cz-num inline-flex items-center gap-1.5 text-[12px] text-ink-subtle" data-id="element-3137" data-mp="27wj92kzkhgvm">
          <ClockIcon className="h-3 w-3" data-id="element-3138" data-mp="2vm44428zw4l9" />
          {provider.responseTime} · {provider.availabilityLabel}
        </p>
        <ReportControls subject={provider.displayName} surface="listing" allowBlock data-id="element-3139" data-mp="30fgep8guaav0" />
      </div>

      <Modal open={hireOpen} onClose={() => setHireOpen(false)} title={`Order — ${selected?.name ?? ''}`} description={`${provider.displayName} (@${provider.handle}) · ${provider.category}`} footer={<>
            <Button variant="secondary" onClick={() => setHireOpen(false)} data-id="element-3141" data-mp="tpwihjv8az68">
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setHireOpen(false)} data-id="element-3142" data-mp="5z92scf2n594">
              Place order request
            </Button>
          </>} data-id="element-3140" data-mp="oxbux02x1ib5">
        {selected && <div className="space-y-4" data-id="element-3143" data-mp="fyrhadc7pyts">
            <dl className="divide-y divide-line rounded-lg border border-line" data-id="element-3144" data-mp="fzt5lji8ohtg">
              {[{
            label: 'Service',
            value: selected.name
          }, {
            label: selected.sessionMinutes ? 'Session' : 'Delivery',
            value: selected.sessionMinutes ? `${selected.sessionMinutes} minutes` : `${selected.deliveryDays} days`
          }, {
            label: 'Price',
            value: naira(selected.priceNaira)
          }, {
            label: 'Campuzon fee',
            value: naira(fee)
          }, {
            label: 'Provider receives',
            value: naira(selected.priceNaira - fee)
          }].map(row => <div key={row.label} className="flex justify-between gap-4 px-4 py-2.5" data-id="element-3145" data-mp="28dg9c5o26nyg">
                  <dt className="text-[13px] text-ink-muted" data-id="element-3146" data-mp="1as243bodwiu7">{row.label}</dt>
                  <dd className="cz-num text-[13px] font-medium text-ink" data-id="element-3147" data-mp="1fpu46sz6nb9h">{row.value}</dd>
                </div>)}
            </dl>
            <div className="rounded-lg border border-line bg-canvas px-4 py-3" data-id="element-3148" data-mp="1knlytur7pbhd">
              <p className="text-[13px] font-medium text-ink" data-id="element-3149" data-mp="1pgyjjtsrsy4u">How payment works</p>
              <p className="mt-1 text-[12px] leading-5 text-ink-muted" data-id="element-3150" data-mp="s114clffwj9l">
                The order moves Requested → Accepted → Funded → In escrow → In progress → Delivered →
                Buyer review → Completed → Released. Funds are never released to the provider on
                payment alone.
              </p>
              <div className="mt-3" data-id="element-3151" data-mp="369lm6spb1hpj">
                <FeatureGate featureKey="escrow" data-id="element-3152" data-mp="366689t7daepp" />
              </div>
            </div>
          </div>}
      </Modal>
    </article>;
}